import React, { useRef } from 'react';
import { Doughnut, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import useIsDarkMode from '../hooks/useIsDarkMode';

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * DonutChart reutilizable para mostrar proporciones
 * Props:
 * - data: [{ label, value, color }]
 * - title: string (opcional)
 * - showLegend: boolean (opcional, default true)
 * - chartHeightClass: string (opcional)
 * - maxWidth: number (opcional)
 */
const DonutChart = ({
  data,
  title,
  showLegend = true,
  chartHeightClass = 'h-56',
  maxWidth = 260,
  onSegmentClick,
}) => {
  const chartRef = useRef(null);
  const isDark = useIsDarkMode();

  // Acortar nombre de 'Videojuegos' a 'vGames' solo en el gráfico
  const chartData = {
    labels: data.map(d => d.label === 'Videojuegos' ? 'vGames' : d.label),
    datasets: [
      {
        data: data.map(d => d.value),
        backgroundColor: data.map(d => d.color),
        borderWidth: 4, // Bordes visibles
        borderColor: isDark ? '#181f2a' : '#ffffff', // Separador según el tema
        borderRadius: 20, // Redondeado
        spacing: 4, // Separación entre segmentos
      },
    ],
  };

  const options = {
    cutout: '80%', // Más fino
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: isDark ? '#e2e8f0' : '#334155',
          font: { size: 9, weight: 'bold' }, // Aún más pequeño
          usePointStyle: true,
          padding: 6,
          boxWidth: 12,
          boxHeight: 12,
          pointStyle: 'circle',
        },
        align: 'center',
        maxHeight: 36, // 2 líneas más compactas
        maxWidth: 220,
        fullSize: false,
        rtl: false,
        display: showLegend,
      },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.label}: ${ctx.parsed} preguntas`,
        },
      },
    },
    layout: {
      padding: 0,
    },
    animation: { duration: 400 },
  };

  const handleChartClick = (event) => {
    if (!onSegmentClick || !chartRef.current) {
      return;
    }

    const elements = getElementAtEvent(chartRef.current, event);

    if (!elements.length) {
      return;
    }

    const segmentIndex = elements[0].index;
    const selectedSegment = data[segmentIndex];

    if (selectedSegment) {
      onSegmentClick(selectedSegment);
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-center">
      {title && <h4 className="font-semibold mb-2 text-center dark:text-white">{title}</h4>}
      <div className="w-full flex flex-col items-center justify-center">
        <div className="w-full flex flex-col items-center justify-center" style={{ maxWidth }}>
          <div className={`w-full ${chartHeightClass} flex items-center justify-center ${onSegmentClick ? 'cursor-pointer' : ''}`}>
            <Doughnut ref={chartRef} data={chartData} options={options} onClick={handleChartClick} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonutChart;
